/**
 * Customer account portal: the shared contract (docs/PORTAL-PLAN.md sections 2.3, 3.4, 4 and 5).
 *
 * Imported by the portal server (apps/portal/server), the portal web app
 * (apps/portal/web), the sync job (src/portal/sync) and the staff routes in the
 * dashboard. Because the browser bundles it, this file is pure: types,
 * constants and tiny pure helpers. No I/O, no node: imports, no dependencies.
 * Anything that needs node:crypto lives in src/portal/keys.ts.
 *
 * Section references below are to docs/PORTAL-PLAN.md.
 */

// ---------------------------------------------------------------------------
// 5.1 Common API types
// ---------------------------------------------------------------------------

export type Lang = 'en' | 'es';
export const LANGS: readonly Lang[] = ['en', 'es'];
export const DEFAULT_LANG: Lang = 'en';

export type Bucket = 'current' | '1-30' | '31-60' | '61-90' | '90+';
export const BUCKETS: readonly Bucket[] = ['current', '1-30', '31-60', '61-90', '90+'];

export interface Money {
  amount: number;
  currency: string;
}

export type ErrorCode =
  | 'invalid_request' // 400 malformed body or query
  | 'invalid_email' // 400 address fails syntax rules
  | 'bad_origin' // 403 Origin or Content-Type check failed
  | 'csrf_failed' // 403 missing or wrong X-Portal-CSRF
  | 'no_challenge' // 401 verify or resend without a live challenge cookie
  | 'invalid_code' // 400 wrong code; attemptsRemaining set
  | 'code_expired' // 410 expired, consumed or superseded challenge
  | 'too_many_attempts' // 429 challenge dead or address locked; retryAfterSeconds set when locked
  | 'rate_limited' // 429 retryAfterSeconds set
  | 'unauthenticated' // 401 no session cookie or unknown session
  | 'session_expired' // 401 idle or absolute timeout
  | 'not_found' // 404 includes a cid or iid outside the session's customers
  | 'pdf_not_available' // 404 invoice exists but no PDF cached yet
  | 'portal_unavailable' // 503 kill switch; message set
  | 'internal'; // 500

/** HTTP status for each error code, exactly as annotated in section 5.1. */
export const ERROR_HTTP_STATUS: Readonly<Record<ErrorCode, number>> = {
  invalid_request: 400,
  invalid_email: 400,
  bad_origin: 403,
  csrf_failed: 403,
  no_challenge: 401,
  invalid_code: 400,
  code_expired: 410,
  too_many_attempts: 429,
  rate_limited: 429,
  unauthenticated: 401,
  session_expired: 401,
  not_found: 404,
  pdf_not_available: 404,
  portal_unavailable: 503,
  internal: 500,
};

export interface ApiError {
  error: ErrorCode;
  retryAfterSeconds?: number;
  attemptsRemaining?: number;
  /** Only with portal_unavailable. */
  message?: { en: string; es: string };
}

export interface CustomerRef {
  /** Opaque cid. */
  id: string;
  name: string;
  currency: string;
  parentId: string | null;
  balance: Money;
  /** Sum of open balances past due. */
  overdue: Money;
  /**
   * Present (and only ever `true`) for the verification test account of docs/PORTAL-PLAN.md 8.1:
   * the web app shows a TEST ACCOUNT banner. Absent for every real customer, so it cannot be
   * confused with data and reveals nothing else about the stored document.
   */
  test?: true;
}

export interface InvoiceSummary {
  /** Opaque iid. */
  id: string;
  docNumber: string;
  txnDate: string;
  dueDate: string | null;
  total: Money;
  balance: Money;
  status: 'open' | 'overdue' | 'paid';
  partiallyPaid: boolean;
  daysOverdue: number;
  managedByStripe: boolean;
}

export type PayAction =
  /** Existing Stripe payment link for the exact balance. */
  | { kind: 'link'; url: string; amount: Money }
  /** Stripe-sourced invoice: pay from Stripe's email or card on file. */
  | { kind: 'stripe_managed' }
  /** No online payment available. */
  | { kind: 'contact' };

export interface InvoiceLine {
  description: string;
  quantity: number | null;
  unitPrice: number | null;
  amount: number;
}

export interface InvoiceDetail extends InvoiceSummary {
  terms: string | null;
  memo: string | null;
  tax: Money;
  subtotal: Money;
  lines: InvoiceLine[];
  payments: Array<{ id: string; date: string; amount: Money }>;
  /** null when paid. */
  pay: PayAction | null;
  pdfAvailable: boolean;
  billTo: { name: string; address: string[] };
}

export interface Payment {
  id: string;
  date: string;
  amount: Money;
  reference: string | null;
  applied: Array<{ invoiceId: string; docNumber: string; amount: Money }>;
  unapplied: Money;
}

export interface StatementLine {
  date: string;
  kind: 'invoice' | 'payment' | 'credit';
  /** Invoice or credit memo number, payment reference. */
  reference: string;
  invoiceId: string | null;
  charge: number | null;
  credit: number | null;
  /** null when `reliable` is false. */
  runningBalance: number | null;
}

export interface Statement {
  customer: { id: string; name: string; billTo: string[] };
  currency: string;
  from: string;
  to: string;
  reliable: boolean;
  /** null when `reliable` is false. */
  openingBalance: number | null;
  totalCharges: number;
  totalCredits: number;
  /** null when `reliable` is false. */
  closingBalance: number | null;
  lines: StatementLine[];
  /** As of dataAsOf. */
  aging: Record<Bucket, number>;
  generatedAt: string;
  dataAsOf: string;
  /** Earliest allowed `from`. */
  historyFrom: string;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// 5.2 Endpoint request and response bodies
// ---------------------------------------------------------------------------

/** GET /api/status (no session). */
export interface StatusResponse {
  enabled: boolean;
  message: { en: string; es: string } | null;
}

/** POST /auth/code/request */
export interface CodeRequestBody {
  email: string;
  lang: Lang;
}
/** 202 body of /auth/code/request and /auth/code/resend. */
export interface CodeSentResponse {
  resendAfterSeconds: number;
  expiresInSeconds: number;
}
/** POST /auth/code/resend: body is `{}`. */
export type CodeResendBody = Record<string, never>;

/** POST /auth/code/verify. Exactly 6 digits after stripping spaces. */
export interface VerifyBody {
  code: string;
}
export interface VerifyResponse {
  customers: CustomerRef[];
  csrfToken: string;
  lang: Lang;
}

/** GET /api/me */
export interface MeResponse {
  maskedEmail: string;
  lang: Lang;
  customers: CustomerRef[];
  csrfToken: string;
  idleExpiresAt: string;
  absoluteExpiresAt: string;
  dataAsOf: string;
}

/** PUT /api/preferences */
export interface PreferencesBody {
  lang: Lang;
}
export interface PreferencesResponse {
  lang: Lang;
}

/** GET /api/customers/:cid/overview (5 recent invoices and payments). */
export interface OverviewResponse {
  customer: CustomerRef;
  balance: Money;
  openInvoicesTotal: Money;
  unappliedCredits: Money;
  overdue: Money;
  aging: Record<Bucket, number>;
  nextDue: { invoiceId: string; docNumber: string; dueDate: string; balance: Money } | null;
  recentInvoices: InvoiceSummary[];
  recentPayments: Payment[];
  dataAsOf: string;
}

export type InvoiceStatusFilter = 'open' | 'paid' | 'all';
/** GET /api/customers/:cid/invoices query (all optional, as strings on the wire). */
export interface InvoiceListQuery {
  status?: InvoiceStatusFilter;
  /** txnDate lower bound, YYYY-MM-DD. */
  from?: string;
  /** txnDate upper bound, YYYY-MM-DD. */
  to?: string;
  /** docNumber prefix, max INVOICE_SEARCH_MAX_LENGTH chars. */
  q?: string;
  cursor?: string;
  /** 1 to INVOICE_PAGE_MAX, default INVOICE_PAGE_DEFAULT. */
  limit?: number;
}

/** GET /api/customers/:cid/payments query. */
export interface PaymentListQuery {
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

/** GET /api/customers/:cid/statement and statement.pdf query. `lang` only on the PDF. */
export interface StatementQuery {
  from?: string;
  to?: string;
  lang?: Lang;
}

/** Route templates, so server routers and the web client cannot drift. */
export const PORTAL_ROUTES = {
  health: '/healthz',
  status: '/api/status',
  codeRequest: '/auth/code/request',
  codeResend: '/auth/code/resend',
  codeVerify: '/auth/code/verify',
  logout: '/auth/logout',
  logoutAll: '/auth/logout-all',
  me: '/api/me',
  preferences: '/api/preferences',
  overview: '/api/customers/:cid/overview',
  invoices: '/api/customers/:cid/invoices',
  invoice: '/api/customers/:cid/invoices/:iid',
  invoicePdf: '/api/customers/:cid/invoices/:iid/pdf',
  payments: '/api/customers/:cid/payments',
  statement: '/api/customers/:cid/statement',
  statementPdf: '/api/customers/:cid/statement.pdf',
} as const;

/** SPA routes (section 5.3). */
export const WEB_ROUTES = {
  login: '/login',
  loginCode: '/login/code',
  accounts: '/accounts',
  overview: '/a/:cid',
  invoices: '/a/:cid/invoices',
  invoice: '/a/:cid/invoices/:iid',
  payments: '/a/:cid/payments',
  statement: '/a/:cid/statement',
  signedOut: '/signed-out',
  sessionExpired: '/session-expired',
  unavailable: '/unavailable',
} as const;

// ---------------------------------------------------------------------------
// 4 Security constants
// ---------------------------------------------------------------------------

/** Cookie, header and token names (4.1, 4.4, 4.5). */
export const CHALLENGE_COOKIE = '__Host-portal_challenge';
/** Brief decision (overrides the plan's `__Host-portal_session`): the session cookie is `__Host-portal_sid`, Path=/. */
export const SESSION_COOKIE = '__Host-portal_sid';
export const CSRF_HEADER = 'X-Portal-CSRF';

/** One-time code (4.1). */
export const OTP_LENGTH = 6;
export const OTP_TTL_SECONDS = 10 * 60;
export const OTP_MAX_ATTEMPTS = 5;
export const OTP_RESEND_COOLDOWN_SECONDS = 60;
export const OTP_MAX_SENDS_PER_CHALLENGE = 3;
/** 10 failed verifications per address in 24 h lock verification for 1 h. */
export const OTP_LOCK_FAILURES = 10;
export const OTP_LOCK_WINDOW_SECONDS = 24 * 60 * 60;
export const OTP_LOCK_SECONDS = 60 * 60;
/**
 * At most one code comparison per challenge in this many seconds (one live challenge per
 * address, so per address). A burst of parallel guesses otherwise all pass the failed-
 * verification lock check before any of them records its failure. A person never types
 * two codes inside it; a refused attempt answers rate_limited and costs no attempt.
 */
export const OTP_VERIFY_SPACING_SECONDS = 2;
/** Challenge cookie Max-Age equals the code lifetime. */
export const CHALLENGE_COOKIE_MAX_AGE_SECONDS = OTP_TTL_SECONDS;
/** otpChallenges.expireAt (Firestore TTL) = expiresAt + 1 day. */
export const OTP_CHALLENGE_TTL_GRACE_SECONDS = 24 * 60 * 60;

/** Sessions (4.4). */
export const SESSION_IDLE_SECONDS = 30 * 60;
export const SESSION_ABSOLUTE_SECONDS = 8 * 60 * 60;
/** lastSeenAt is written at most once per this many seconds. */
export const SESSION_TOUCH_INTERVAL_SECONDS = 60;
export const SESSION_MAX_PER_ADDRESS = 5;
export const SESSION_TOKEN_BYTES = 32;
export const CHALLENGE_ID_BYTES = 32;

/** Timing floor of POST /auth/code/request (4.3). */
export const CODE_REQUEST_FLOOR_MS = 700;
export const CODE_REQUEST_JITTER_MS = 100;

/** A rate-limit rule: at most `max` events per `windowSeconds`. */
export interface RateRule {
  max: number;
  windowSeconds: number;
}

/** Rate limits (4.2). */
export const RATE_LIMITS = {
  addressCode: [
    { max: 1, windowSeconds: 60 },
    { max: 3, windowSeconds: 15 * 60 },
    { max: 10, windowSeconds: 24 * 60 * 60 },
  ],
  ipCodeRequests: [
    { max: 10, windowSeconds: 15 * 60 },
    { max: 50, windowSeconds: 24 * 60 * 60 },
  ],
  ipVerifyAttempts: [{ max: 30, windowSeconds: 15 * 60 }],
  ipAuthAll: [{ max: 120, windowSeconds: 15 * 60 }],
  globalCodesSent: [
    { max: 100, windowSeconds: 60 * 60 },
    { max: 500, windowSeconds: 24 * 60 * 60 },
  ],
  sessionApiReads: [{ max: 600, windowSeconds: 15 * 60 }],
  sessionPdfs: [{ max: 60, windowSeconds: 15 * 60 }],
} as const satisfies Record<string, readonly RateRule[]>;
export type RateLimitKind = keyof typeof RATE_LIMITS;

/** Rate-limit counter documents expire this long after their window starts (TTL slack). */
export const RATE_LIMIT_TTL_GRACE_SECONDS = 60 * 60;

/** auditEvents.expireAt = at + 400 days (3.4). */
export const AUDIT_RETENTION_DAYS = 400;

/** Settings cache for the staff kill switch (4.7). */
export const SETTINGS_CACHE_SECONDS = 30;

// ---------------------------------------------------------------------------
// 2.3 and 3.5 Identity and sync constants
// ---------------------------------------------------------------------------

/** Hard cap on customers per address (2.3 rule 6). Above it the address is left out of the index. */
export const MAX_CUSTOMERS_PER_ADDRESS = 30;
export const HISTORY_MONTHS_DEFAULT = 24;
/** BillEmail / BillEmailCc window (2.3 rule 2). */
export const BILL_EMAIL_DAYS_DEFAULT = 180;
export const PDF_LIMIT_DEFAULT = 300;
export const PDF_LIMIT_BACKFILL = 6000;
export const EMAIL_MAX_LENGTH = 254;
/** Hosts allowed in PortalInvoiceDoc.payUrl, at sync time and again at render time (3.5, 4.5). */
export const PAY_URL_HOST_ALLOWLIST: readonly string[] = ['pay.spoonity.com', 'buy.stripe.com', 'checkout.stripe.com'];
/** Sync guards (3.5 step 4): a run is refused below these fractions of the previous run. */
export const SYNC_MIN_INVOICE_RATIO = 0.7;
export const SYNC_MIN_CUSTOMER_RATIO = 0.8;
export const SYNC_MIN_INDEX_RATIO = 0.8;
export const RECONCILE_TOLERANCE = 0.01;

/** Paging and field limits (5.2, 3.4). */
export const INVOICE_PAGE_DEFAULT = 25;
export const INVOICE_PAGE_MAX = 100;
export const INVOICE_SEARCH_MAX_LENGTH = 20;
export const MEMO_MAX_LENGTH = 1000;
export const PAYMENT_REFERENCE_MAX_LENGTH = 40;
/** Overview shows this many recent invoices and payments. */
export const OVERVIEW_RECENT_COUNT = 5;
/** Statement default `from` is the first day of the month this many months back (5.2). */
export const STATEMENT_DEFAULT_MONTHS_BACK = 3;

// ---------------------------------------------------------------------------
// 3.4 Firestore data model (database `portal`)
// ---------------------------------------------------------------------------

/** Named database and top-level collection names. Subcollections hang under customers/{cid}. */
export const PORTAL_DATABASE_ID = 'portal';
export const COLLECTIONS = {
  customers: 'customers',
  invoices: 'invoices',
  payments: 'payments',
  credits: 'credits',
  emailIndex: 'emailIndex',
  otpChallenges: 'otpChallenges',
  sessions: 'sessions',
  rateLimits: 'rateLimits',
  auditEvents: 'auditEvents',
  blocklist: 'blocklist',
  contacts: 'contacts',
  settings: 'settings',
  prefs: 'prefs',
  meta: 'meta',
  syncRuns: 'syncRuns',
} as const;
/** Fixed document ids. */
export const SETTINGS_DOC_ID = 'portal';
export const META_SYNC_DOC_ID = 'sync';
/** meta/syncLease: one portal-sync run at a time (overlapping runs would fight over PDFs and docs). */
export const META_SYNC_LEASE_DOC_ID = 'syncLease';

/**
 * Opaque id kinds. cid / iid / pid / mid = `<prefix>_` + base32 of the first
 * 80 bits of HMAC(idKey, "<kind>:" + qboId). See src/portal/keys.ts.
 */
export type IdKind = 'customer' | 'invoice' | 'payment' | 'credit';
export const ID_PREFIX: Readonly<Record<IdKind, string>> = { customer: 'c', invoice: 'i', payment: 'p', credit: 'm' };

export interface PortalAddress {
  line1: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
}

/** customers/{cid} */
export interface PortalCustomerDoc {
  /** Never sent to the browser. */
  qboId: string;
  /** DisplayName, else CompanyName. */
  name: string;
  /** QuickBooks customers are single-currency. */
  currency: string;
  parentCid: string | null;
  language: Lang;
  billingAddress: PortalAddress;
  /** QuickBooks Customer.Balance (amount owing, net of credits). */
  balance: number;
  openInvoicesTotal: number;
  /** Unapplied payments + credit memo remaining. */
  unappliedCredits: number;
  aging: Record<Bucket, number>;
  nextDue: { iid: string; docNumber: string; dueDate: string; balance: number } | null;
  /** Section 3.6. */
  reconciled: boolean;
  /** YYYY-MM-DD, start of the history window. */
  historyFrom: string;
  /** ISO, finish time of the sync that wrote it. */
  dataAsOf: string;
  /** Skip unchanged writes. */
  contentHash: string;
  /** Test fixtures only; the sync never touches these. */
  test?: true;
}

export type InvoiceSource = 'stripe' | 'consumption-billing' | 'qbo';

/** customers/{cid}/invoices/{iid} */
export interface PortalInvoiceDoc {
  qboId: string;
  docNumber: string;
  txnDate: string;
  dueDate: string | null;
  total: number;
  balance: number;
  tax: number;
  currency: string;
  /** "Net 30" etc. */
  terms: string | null;
  /** CustomerMemo only, max MEMO_MAX_LENGTH chars. */
  memo: string | null;
  lines: InvoiceLine[];
  source: InvoiceSource;
  /** Stripe payment link, only when open and matching balance, host-allowlisted. */
  payUrl: string | null;
  pdf: { object: string; syncToken: string } | null;
  payments: Array<{ pid: string; date: string; amount: number }>;
  contentHash: string;
}

/** customers/{cid}/payments/{pid} */
export interface PortalPaymentDoc {
  qboId: string;
  date: string;
  amount: number;
  currency: string;
  /** PaymentRefNum, max PAYMENT_REFERENCE_MAX_LENGTH chars. */
  reference: string | null;
  applied: Array<{ iid: string; docNumber: string; amount: number }>;
  unapplied: number;
  contentHash: string;
}

/** customers/{cid}/credits/{mid} */
export interface PortalCreditDoc {
  qboId: string;
  docNumber: string;
  date: string;
  amount: number;
  remaining: number;
  currency: string;
  memo: string | null;
  contentHash: string;
}

export type EmailSource = 'primary' | 'bill' | 'cc' | 'staff';

/** emailIndex/{HMAC(indexKey, normalizedEmail) hex} */
export interface EmailIndexDoc {
  cids: string[];
  maskedEmail: string;
  updatedAt: string;
  sources: EmailSource[];
}

/** otpChallenges/{challengeId} (TTL on expireAt). */
export interface OtpChallengeDoc {
  /** HMAC(indexKey, email): same key space as the index. */
  emailHash: string;
  /** false = decoy for an unknown or blocked address, never verifiable. */
  known: boolean;
  /** HMAC(otpPepper, challengeId + ":" + code); null for decoys. */
  codeHash: string | null;
  lang: Lang;
  createdAt: string;
  expiresAt: string;
  /** expiresAt + 1 day (TTL). */
  expireAt: Date;
  attempts: number;
  consumedAt: string | null;
  supersededAt: string | null;
  ipHash: string;
  resendCount: number;
  lastSentAt: string | null;
  /** When the last WRONG code was compared (OTP_VERIFY_SPACING_SECONDS). Optional: older documents lack it. */
  lastAttemptAt?: string | null;
  /**
   * The normalized address, AES-256-GCM sealed under a key derived from the OTP
   * pepper with the challenge id as associated data (apps/portal/server/src/auth/seal.ts).
   * Needed because a resend mails the SAME challenge and the browser only holds the
   * challenge id. Cleared when the challenge is consumed; the document's TTL removes
   * the rest. Optional so documents written before it existed still read.
   */
  sealedEmail?: string | null;
}

/** sessions/{sha256(sessionToken) hex} (TTL on expireAt). */
export interface SessionDoc {
  emailHash: string;
  maskedEmail: string;
  createdAt: string;
  lastSeenAt: string;
  idleExpiresAt: string;
  absoluteExpiresAt: string;
  expireAt: Date;
  revokedAt: string | null;
  ipHash: string;
  uaFamily: string;
  lang: Lang;
}

/** rateLimits/{bucketKey} (TTL on expireAt). bucketKey = "<kind>:<hash>:<windowSeconds>:<windowStartEpoch>" (src/portal/limits.ts). */
export interface RateLimitDoc {
  count: number;
  expireAt: Date;
}

export const AUDIT_EVENT_TYPES = [
  'code_requested',
  'code_sent',
  'code_suppressed',
  'code_verify_failed',
  'code_locked',
  'login',
  'logout',
  'logout_all',
  'session_expired',
  'session_revoked',
  'customer_viewed',
  'invoice_pdf_downloaded',
  'statement_downloaded',
  'rate_limited',
  'global_cap',
  'blocked_email',
  'portal_disabled',
  'staff_block',
  'staff_unblock',
  'staff_revoke',
  'staff_setting',
  'staff_contact',
  // portal-sync: an address gained or lost customers in the email index (the authorization boundary).
  'access_granted',
  'access_revoked',
] as const;
export type AuditEventType = (typeof AUDIT_EVENT_TYPES)[number];

/** auditEvents/{ts-rand} (TTL on expireAt = at + AUDIT_RETENTION_DAYS). */
export interface AuditEventDoc {
  at: string;
  type: AuditEventType;
  emailHash: string | null;
  maskedEmail: string | null;
  /** Customers the event concerns (login: all customers of the address). */
  cids: string[];
  /** IPv4 /24 or IPv6 /48, never the full address. */
  ipPrefix: string | null;
  uaFamily: string | null;
  outcome: 'ok' | 'denied' | 'error';
  detail: string | null;
  expireAt: Date;
}

/** blocklist/{emailHash} */
export interface BlockDoc {
  maskedEmail: string;
  reason: string;
  by: string;
  at: string;
}

/** contacts/{cid}: staff overrides, read by the sync only. The portal server never reads it. */
export interface ContactOverrideDoc {
  add: Array<{ email: string; by: string; at: string }>;
  remove: Array<{ email: string; by: string; at: string }>;
}

/** settings/portal */
export interface PortalSettingsDoc {
  enabled: boolean;
  message: { en: string; es: string } | null;
  updatedBy: string;
  updatedAt: string;
}

/** prefs/{emailHash} */
export interface PrefsDoc {
  lang: Lang;
  updatedAt: string;
}

export type SyncRunStatus = 'running' | 'ok' | 'partial' | 'error' | 'refused';

/** syncRuns/{runId} and meta/sync */
export interface PortalSyncRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: SyncRunStatus;
  counts: {
    customers: number;
    invoices: number;
    payments: number;
    credits: number;
    indexEntries: number;
    deleted: Record<string, number>;
    pdfsFetched: number;
    pdfsPending: number;
    unreconciled: number;
  };
  errors: string[];
}

// ---------------------------------------------------------------------------
// Pure helpers: addresses (2.3 rule 1, 4.9) and blocked domains
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]{1,64}@[a-z0-9.-]{1,252}\.[a-z]{2,}$/;

/** C0 and C1 control characters and DEL: never part of a deliverable address (RFC 5321). */
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f]/;

/**
 * Section 2.3 rule 1 for ONE address: trim, NFC, lowercase, then accept only
 * `^[^\s@]{1,64}@[a-z0-9.-]{1,252}\.[a-z]{2,}$` with total length at most 254
 * and no control character anywhere (a NUL or C1 byte in the local part would
 * otherwise reach hashes, masks and audit rows).
 * No plus-tag or dot stripping, no domain matching. Returns null when invalid.
 * The login form, the sync and the staff routes all use this one function.
 */
export function normalizeEmail(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  const e = raw.trim().normalize('NFC').toLowerCase();
  if (e.length === 0 || e.length > EMAIL_MAX_LENGTH) return null;
  if (CONTROL_RE.test(e)) return null;
  return EMAIL_RE.test(e) ? e : null;
}

/**
 * Splits a QuickBooks free-text address field (`,` `;` or whitespace separated)
 * and returns the distinct valid normalized addresses in first-seen order.
 */
export function extractEmails(field: string | null | undefined): string[] {
  if (!field) return [];
  const out: string[] = [];
  for (const part of String(field).split(/[,;\s]+/)) {
    const e = normalizeEmail(part);
    if (e && !out.includes(e)) out.push(e);
  }
  return out;
}

/** Domain part of an already normalized address ('' when there is no '@'). */
export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at < 0 ? '' : email.slice(at + 1);
}

/**
 * `j***@a***.com`: first character of the local part + `***@` + first
 * character of the domain + `***.` + TLD (4.9). Input must be normalized.
 */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at < 1) return '***';
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  const first = domain.slice(0, 1);
  const tld = dot >= 0 ? domain.slice(dot + 1) : '';
  return `${local.slice(0, 1)}***@${first}***.${tld}`;
}

/** Domains that can never sign in to the customer portal (staff use the dashboard). */
export const PORTAL_BLOCKED_DOMAINS_DEFAULT: readonly string[] = ['spoonity.com'];

/**
 * THE single definition of the blocked domain list, used by the sync (to leave
 * addresses out of the index) and by the auth backend (to answer with a decoy).
 *
 * Reads `PORTAL_BLOCKED_DOMAINS` (comma list, case-insensitive, whitespace and
 * a leading `@` tolerated). Unset: ['spoonity.com']. Set to the empty string
 * (or only separators): no domains at all. For continuity with the plan's
 * infra table, `PORTAL_EXCLUDED_DOMAINS` is read when `PORTAL_BLOCKED_DOMAINS`
 * is unset.
 */
export function portalBlockedDomains(env: Readonly<Record<string, string | undefined>>): string[] {
  const raw = env['PORTAL_BLOCKED_DOMAINS'] ?? env['PORTAL_EXCLUDED_DOMAINS'];
  if (raw === undefined) return [...PORTAL_BLOCKED_DOMAINS_DEFAULT];
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const d = part.trim().toLowerCase().replace(/^@/, '');
    if (d && !out.includes(d)) out.push(d);
  }
  return out;
}

/**
 * True when the address is on a blocked domain OR ANY SUBDOMAIN of one:
 * `spoonity.com` blocks `x@spoonity.com` and `x@mail.spoonity.com`, never
 * `x@notspoonity.com`, `x@spoonity.com.mx` or `x@spoonity.com.evil.example`
 * (the match is on whole labels from the right). Every mailbox under a blocked
 * domain belongs to the same organisation (for spoonity.com: staff, who use the
 * dashboard), so a subdomain is never a reason to let it sign in as a customer.
 */
export function isBlockedDomain(email: string, blockedDomains: readonly string[]): boolean {
  const d = emailDomain(email.trim().toLowerCase());
  return d !== '' && blockedDomains.some((b) => b !== '' && (d === b || d.endsWith(`.${b}`)));
}

/** True when `url` is https and its host is in PAY_URL_HOST_ALLOWLIST (sync time and render time). */
export function isAllowedPayUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && PAY_URL_HOST_ALLOWLIST.includes(u.hostname);
  } catch {
    return false;
  }
}

/** Money in major units rounded to cents. */
export function roundMoney(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Strips spaces and checks for exactly OTP_LENGTH digits; returns the digits or null. */
export function normalizeOtpCode(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  const digits = raw.replace(/\s+/g, '');
  return new RegExp(`^[0-9]{${OTP_LENGTH}}$`).test(digits) ? digits : null;
}
